import { z } from 'zod';
import { TypedApiClient } from '../index';

// ====== Response Schemas ======

export const AuthUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  organization_id: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  store_id: z.string().optional().nullable(),
  active: z.boolean(),
}).passthrough();

export const SessionSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  expires_at: z.number(),
}).partial().passthrough();

export const SignupResponseSchema = z.object({
  user: AuthUserSchema,
  message: z.string(),
}).passthrough();

export const LoginResponseSchema = z.object({
  user: AuthUserSchema,
  session: SessionSchema,
  accessToken: z.string(),
  refreshToken: z.string(),
}).passthrough();

export const MessageResponseSchema = z.object({
  message: z.string(),
}).passthrough();

export const VerifyEmailResponseSchema = z.object({
  message: z.string(),
  session: SessionSchema.optional(),
}).passthrough();

// ====== Request Schemas ======

export const SignupSchema = z.object({
  email: z.string(),
  password: z.string(),
  name: z.string(),
  phone: z.string().optional(),
  organizationId: z.string(),
  role: z.string().optional().default('cashier'),
}).passthrough();

export const LoginSchema = z.object({
  email: z.string(),
  password: z.string(),
}).passthrough();

export const RequestPasswordResetSchema = z.object({
  email: z.string(),
}).passthrough();

export const ResetPasswordSchema = z.object({
  token: z.string(),
  newPassword: z.string(),
}).passthrough();

export const VerifyEmailSchema = z.object({
  token: z.string(),
}).passthrough();

export const ResendVerificationSchema = z.object({
  email: z.string(),
}).passthrough();

// ====== Type Exports ======

export type AuthUser = z.infer<typeof AuthUserSchema>;
export type Session = z.infer<typeof SessionSchema>;
export type SignupResponse = z.infer<typeof SignupResponseSchema>;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;
export type MessageResponse = z.infer<typeof MessageResponseSchema>;
export type Signup = z.infer<typeof SignupSchema>;
export type Login = z.infer<typeof LoginSchema>;

/**
 * Create auth API factory
 */
export function createAuthApi(client: TypedApiClient) {
  return {
    /**
     * Sign up a new user
     */
    signup: (data: Signup): Promise<SignupResponse> => {
      return client.post('/auth/signup', {
        data,
        requestSchema: SignupSchema,
        responseSchema: SignupResponseSchema,
      });
    },

    /**
     * Log in an existing user
     */
    login: (data: Login): Promise<LoginResponse> => {
      return client.post('/auth/login', {
        data,
        requestSchema: LoginSchema,
        responseSchema: LoginResponseSchema,
      });
    },

    /**
     * Get current session info
     */
    getSession: (): Promise<Session> => {
      return client.get('/auth/session', {
        responseSchema: SessionSchema,
      });
    },

    /**
     * Get current user info
     */
    getMe: (): Promise<AuthUser> => {
      return client.get('/auth/me', {
        responseSchema: AuthUserSchema,
      });
    },

    /**
     * Log out the current user
     */
    logout: (): Promise<MessageResponse> => {
      return client.post('/auth/logout', {
        data: {},
        responseSchema: MessageResponseSchema,
      });
    },

    /**
     * Request a password reset email
     */
    requestPasswordReset: (data: { email: string }): Promise<MessageResponse> => {
      return client.post('/auth/password-reset-request', {
        data,
        requestSchema: RequestPasswordResetSchema,
        responseSchema: MessageResponseSchema,
      });
    },

    /**
     * Reset password with token
     */
    resetPassword: (data: { token: string; newPassword: string }): Promise<MessageResponse> => {
      return client.post('/auth/password-reset', {
        data,
        requestSchema: ResetPasswordSchema,
        responseSchema: MessageResponseSchema,
      });
    },

    /**
     * Verify email with token
     */
    verifyEmail: (data: { token: string }): Promise<z.infer<typeof VerifyEmailResponseSchema>> => {
      return client.post('/auth/verify-email', {
        data,
        requestSchema: VerifyEmailSchema,
        responseSchema: VerifyEmailResponseSchema,
      });
    },

    /**
     * Resend verification email
     */
    resendVerification: (data: { email: string }): Promise<MessageResponse> => {
      return client.post('/auth/resend-verification', {
        data,
        requestSchema: ResendVerificationSchema,
        responseSchema: MessageResponseSchema,
      });
    },

    /**
     * Refresh access token
     */
    refreshToken: (refreshToken: string): Promise<Session> => {
      return client.post('/auth/refresh', {
        data: { refreshToken },
        responseSchema: SessionSchema,
      });
    },
  };
}
